import { useState, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import ExtensionSelectionModal from '../../components/feature/ExtensionSelectionModal';
import SEO from '../../components/feature/SEO';

export default function HowItWorks() {
  const [isExtensionModalOpen, setIsExtensionModalOpen] = useState(false);

  useEffect(() => {
    // Google Analytics
    const script1 = document.createElement('script');
    script1.async = true;
    script1.src = 'https://www.googletagmanager.com/gtag/js?id=G-8VEFRXMPD7';
    document.head.appendChild(script1);

    const script2 = document.createElement('script');
    script2.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', 'G-8VEFRXMPD7');
    `;
    document.head.appendChild(script2);

    return () => {
      document.head.removeChild(script1);
      document.head.removeChild(script2);
    };
  }, []);

  const openExtensionModal = () => {
    setIsExtensionModalOpen(true);
  };

  const closeExtensionModal = () => {
    setIsExtensionModalOpen(false);
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="How XeroVerifier Works – Xero Invoice Verification Guide"
        description="Discover how XeroVerifier protects Xero invoices in 3 simple steps. Cryptographic verification stamps prevent invoice fraud and build client trust. Install in minutes."
        keywords="how invoice verification works, Xero invoice stamping, bioVerifier extension, invoice fraud prevention steps, Xero security setup"
        canonical="/how-it-works"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'HowTo',
          name: 'How to Use XeroVerifier for Invoice Verification',
          description: 'A 3-step guide to protecting your Xero invoices with cryptographic verification.',
          step: [
            {
              '@type': 'HowToStep',
              name: 'Install the bioVerifier Extension',
              text: 'Install the bioVerifier Chrome or Firefox extension from our site or the browser store.',
            },
            {
              '@type': 'HowToStep',
              name: 'Invoice Verification',
              text: 'Every invoice created contains a unique verification code and QR code for authenticity.',
            },
            {
              '@type': 'HowToStep',
              name: 'Verification Popup',
              text: 'A popup verifies the authenticity of the email, invoice contents, and the sender.',
            },
          ],
        }}
      />
      <Header />
      
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            How To Use bioVerifier Invoice Verification.&nbsp;
          </h1>
          <p className="text-xl text-gray-600 max-w-5xl mx-auto whitespace-nowrap mb-3">
            Discover how our simple 3-step process protects your invoices and builds client trust in just minutes.
            <br />
          </p>
          <p className="text-gray-700 text-lg leading-relaxed mb-6">
            When the receiving party clicks on the XeroVerifier Stamp, using the proprietary and patented free browser extension, they will immediately see who sent the invoice, when, to who, and other relevant information. A warning will appear if the system detects the XeroVerifier Stamp has been tampered with.
          </p>
        </div>
      </section>

      {/* Steps Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="space-y-20">
            {/* Step 1 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-xl">
                    1
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900">Install the bioVerifier extension.</h2>
                </div>
                <p className="text-xl text-gray-600 mb-6">
                  Install the bioVerifier Chrome or Firefox extension. This can be done by downloading and installing it here from our site, or on the Chrome and Firefox stores, for free.&nbsp;
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3">
                    <i className="ri-check-line text-green-500 text-xl"></i>
                    <span className="text-gray-700">One-click Chrome Web Store installation</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <i className="ri-check-line text-green-500 text-xl"></i>
                    <span className="text-gray-700">Secure connection to verify Senders and Invoices and Estimates</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <i className="ri-check-line text-green-500 text-xl"></i>
                    <span className="text-gray-700">No sensitive data stored or collected</span>
                  </li>
                </ul>
              </div>
              <div className="relative">
                <img 
                  src="https://static.readdy.ai/image/60cfa5760427af1c3d73f4665d1a36c9/01e896b44506b4ae17901ef9fd7312f7.png"
                  alt="Installation process"
                  className="rounded-xl shadow-2xl w-full"
                />
              </div>
            </div>

            {/* Step 2 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div className="order-2 lg:order-1 relative">
                <img 
                  src="https://static.readdy.ai/image/60cfa5760427af1c3d73f4665d1a36c9/346b6990e9eb893ac4ae15235bc4c05d.jpeg"
                  alt="Automatic stamping"
                  className="rounded-xl shadow-2xl w-full"
                />
              </div>
              <div className="order-1 lg:order-2">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-xl">
                    2
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900">Invoice Verification</h2>
                </div>
                <p className="text-xl text-gray-600 mb-6">
                  Every invoice and estimate created by the sending business contains a unique verification code and QR code. The Patent Pending process contains hashed cross-reference codes that ensure Truth of Origin.&nbsp;As a consumer, simply 'Right-Click and select the bioMail Verifier icon.&nbsp;
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3">
                    <i className="ri-check-line text-green-500 text-xl"></i>
                    <span className="text-gray-700">Unique cryptographic signature for each invoice and estimate</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <i className="ri-check-line text-green-500 text-xl"></i>
                    <span className="text-gray-700">QR code for instant mobile verification as an option. Note that included embedded hashes cannot be seen by the mobile phone</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <i className="ri-check-line text-green-500 text-xl"></i>
                    <span className="text-gray-700">Tamper-proof verification embedded patent pending verification code</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Step 3 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-xl">
                    3
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900">Verification Popup</h2>
                </div>
                <p className="text-xl text-gray-600 mb-6">
                  A popup will appear verifying the authenticity of the email, the contents of the invoice/estimate, and the sender.&nbsp;You can now feel secure in making that payment.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3">
                    <i className="ri-check-line text-green-500 text-xl"></i>
                    <span className="text-gray-700">Instant validation using the Browser Extension,</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <i className="ri-check-line text-green-500 text-xl"></i>
                    <span className="text-gray-700">Your clients can see that you care about their online financial safety, and proactive fighting fraud. A strong  market and brand advantage. </span>
                  </li>
                  <li className="flex items-center gap-3">
                    <i className="ri-check-line text-green-500 text-xl"></i>
                    <span className="text-gray-700">Clear verification status and business details</span>
                  </li>
                </ul>
              </div>
              <div className="relative">
                <img 
                  src="https://static.readdy.ai/image/60cfa5760427af1c3d73f4665d1a36c9/aac18db93b23ffd2c0594c447bb78af0.jpeg"
                  alt="Client verification"
                  className="rounded-xl shadow-2xl w-full"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technical Details */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Enterprise-Grade Security
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our proprietary and patented verification system uses a chain of advanced cryptography and secure protocols to ensure maximum protection.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white p-6 rounded-xl shadow-lg text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-lock-line text-blue-600 text-2xl"></i>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">256-bit Encryption</h3>
              <p className="text-gray-600 text-sm">Military-grade encryption protects all verification data</p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-lg text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-green-600 text-2xl"></i>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Digital Signatures</h3>
              <p className="text-gray-600 text-sm">Cryptographic signatures prevent tampering and forgery</p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-lg text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-cloud-line text-purple-600 text-2xl"></i>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Cloud Infrastructure</h3>
              <p className="text-gray-600 text-sm">Secure cloud hosting with 99.9% uptime guarantee</p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-lg text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-eye-line text-orange-600 text-2xl"></i>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Audit Trails</h3>
              <p className="text-gray-600 text-sm">Complete verification history for compliance and security</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">
            Start Protecting Your Invoices Today
          </h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Join thousands of businesses using XeroVerifier to secure their invoices and build client trust.
          </p>
          <button 
            className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors shadow-lg whitespace-nowrap"
            onClick={openExtensionModal}
          >
            <i className="ri-download-line mr-2"></i>
            Install Extension Now
          </button>
        </div>
      </section>

      {/* Extension Selection Modal */}
      <ExtensionSelectionModal 
        isOpen={isExtensionModalOpen}
        onClose={closeExtensionModal}
      />

      <Footer />
    </div>
  );
}
